import React from "react";
import { motion } from "framer-motion";
import {
  FiAward,
  FiCheckCircle,
  FiCompass,
  FiHeadphones,
  FiMap,
  FiShield,
} from "react-icons/fi";
import gorilla from "../assets/gorilla.jpg";

const features = [
  {
    icon: FiCompass,
    title: "First-Hand Destination Knowledge",
    text: "Our safari planning is grounded in real park experience, seasonal insight, and practical route knowledge.",
  },
  {
    icon: FiHeadphones,
    title: "Responsive Guest Service",
    text: "From your first inquiry to your final airport transfer, we keep communication clear and organized.",
  },
  {
    icon: FiShield,
    title: "Reliable Safari Logistics",
    text: "Permits, lodges, vehicles, guides, and timing are coordinated carefully so your trip runs smoothly.",
  },
  {
    icon: FiMap,
    title: "Tailor-Made Safari Variety",
    text: "Choose gorilla trekking, wildlife drives, birding, culture, family trips, honeymoons, or beach extensions.",
  },
];

const trustPoints = [
  "Private and group safari options",
  "Luxury, midrange, and family-friendly planning",
  "Trusted local guides and regional partners",
];

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: index * 0.08, ease: "easeOut" },
  }),
};

const Expertise = () => {
  return (
    <section className="bg-white py-20 font-[Poppins]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="relative overflow-hidden rounded-sm bg-[#082f1f] p-7 text-white shadow-2xl shadow-black/15 sm:p-9"
          >
            <img
              src={gorilla}
              alt="Gorilla trekking expertise"
              className="absolute inset-0 h-full w-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#082f1f] via-[#082f1f]/90 to-[#082f1f]/80" />

            <div className="relative">
              <div className="mb-5 inline-flex items-center gap-2 border-l-4 border-[#F5B731] bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#F5B731]">
                <FiAward />
                Why Choose Us
              </div>

              <h2 className="max-w-xl text-4xl font-extrabold uppercase leading-tight tracking-normal sm:text-5xl">
                Trusted Safari Expertise For Better Journeys
              </h2>

              <p className="mt-5 max-w-xl text-base leading-8 text-white/75">
                Gorilland Travel Care helps travelers experience East Africa with
                confidence, comfort, and meaningful local insight.
              </p>

              <div className="mt-8 grid gap-3">
                {trustPoints.map((point) => (
                  <div key={point} className="flex items-start gap-3 text-sm text-white/80">
                    <FiCheckCircle className="mt-1 shrink-0 text-lg text-[#F5B731]" />
                    {point}
                  </div>
                ))}
              </div>

              <div className="mt-9 grid grid-cols-3 border border-white/15 bg-white/10 text-center">
                <div className="p-4">
                  <p className="text-3xl font-extrabold">96%</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60">
                    Service Rating
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-3xl font-extrabold">20+</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60">
                    Years Care
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-3xl font-extrabold">4+</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60">
                    Countries
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          <div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.65, ease: "easeOut" }}
              className="mb-8"
            >
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#C88804]">
                Travel With Specialists
              </p>
              <h3 className="mt-3 max-w-3xl text-3xl font-extrabold uppercase leading-tight text-[#082f1f] sm:text-4xl">
                Professional Planning From Inquiry To Departure
              </h3>
              <p className="mt-4 max-w-2xl text-base leading-8 text-[#5d7168]">
                Every trip is shaped around your travel style, time, budget, and
                wildlife goals, with the details handled by people who know the
                region.
              </p>
            </motion.div>

            <div className="grid gap-5 sm:grid-cols-2">
              {features.map((feature, index) => {
                const Icon = feature.icon;

                return (
                  <motion.article
                    key={feature.title}
                    custom={index}
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    whileHover={{ y: -6 }}
                    className="rounded-sm border border-[#eadfbe] bg-[#fbf8f0] p-6 shadow-lg shadow-black/5 transition"
                  >
                    <div className="mb-5 grid h-12 w-12 place-items-center rounded-sm bg-[#082f1f] text-2xl text-[#F5B731]">
                      <Icon />
                    </div>
                    <h4 className="text-xl font-extrabold text-[#082f1f]">
                      {feature.title}
                    </h4>
                    <p className="mt-3 text-sm leading-7 text-[#5d7168]">
                      {feature.text}
                    </p>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Expertise;
